import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Clock, ArrowUpRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useRecentlyViewed } from '../context/RecentlyViewedContext';

/**
 * Recently Viewed Products Widget
 * Shows a floating toggle button and expandable panel
 * Uses shared context for state management
 */

// Recently Viewed Panel Component
const RecentlyViewedPanel = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const { items, clearAll } = useRecentlyViewed();
  
  if (!isOpen || items.length === 0) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[90] lg:hidden"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className={`fixed z-[95] transition-transform duration-500 ease-out ${
        isDark ? 'bg-zinc-900' : 'bg-white'
      } bottom-0 left-0 right-0 lg:bottom-auto lg:top-1/2 lg:-translate-y-1/2 lg:right-0 lg:left-auto lg:w-80 rounded-t-2xl lg:rounded-l-2xl lg:rounded-tr-none shadow-2xl border-t lg:border-t-0 lg:border-l ${
        isDark ? 'border-white/10' : 'border-black/10'
      }`}
      data-testid="recently-viewed-panel"
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDark ? 'border-white/10' : 'border-black/10'
        }`}>
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
            <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-black'}`}>
              Recently Viewed
            </h3>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              isDark ? 'bg-white/10 text-white/50' : 'bg-black/10 text-black/50'
            }`}>
              {items.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearAll}
              className={`text-[10px] uppercase tracking-wider transition-colors ${
                isDark ? 'text-white/40 hover:text-white' : 'text-black/40 hover:text-black'
              }`}
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-black/10 text-black/60'
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Products List */}
        <div className="p-4 max-h-[60vh] lg:max-h-[400px] overflow-y-auto">
          <div className="space-y-2">
            {items.map((product, index) => (
              <Link
                key={`${product.name}-${index}`}
                to={product.category ? `/products/${product.category}` : '/products'}
                onClick={onClose}
                className={`group flex items-center gap-3 p-2 rounded-lg transition-all ${
                  isDark 
                    ? 'hover:bg-white/5' 
                    : 'hover:bg-black/5'
                }`}
                data-testid={`recently-viewed-item-${index}`}
              >
                {/* Thumbnail */}
                <div className={`w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 ${
                  isDark ? 'bg-zinc-800' : 'bg-zinc-100'
                }`}>
                  <img 
                    src={product.image || "/placeholder-image.webp"} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className={`text-[12px] font-medium line-clamp-1 ${
                    isDark ? 'text-white' : 'text-black'
                  }`}>
                    {product.name}
                  </p>
                  <p className={`text-[10px] line-clamp-1 ${
                    isDark ? 'text-white/40' : 'text-black/40'
                  }`}>
                    {product.specs}
                  </p>
                  {product.badge && (
                    <span className="inline-block text-[8px] px-1.5 py-0.5 mt-1 bg-amber-500/20 text-amber-500 rounded">
                      {product.badge}
                    </span>
                  )}
                </div>

                {/* Arrow */}
                <ArrowUpRight className={`w-3.5 h-3.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                  isDark ? 'text-amber-500' : 'text-amber-600'
                }`} />
              </Link>
            ))}
          </div>
        </div>

        {/* Footer CTA */}
        <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-black/10'}`}>
          <Link 
            to="/products"
            onClick={onClose}
            className={`block w-full py-2.5 text-center text-[11px] font-bold tracking-wider uppercase rounded-lg transition-colors ${
              isDark 
                ? 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white' 
                : 'bg-black/5 text-black/70 hover:bg-black/10 hover:text-black'
            }`}
          >
            Browse All Products
          </Link>
        </div>
      </div>
    </>
  );
};

// Floating Toggle Button
const RecentlyViewedToggle = ({ onClick }) => {
  const { isDark } = useTheme();
  const { items } = useRecentlyViewed();
  
  if (items.length === 0) return null;

  return (
    <button
      onClick={onClick}
      className={`fixed left-6 bottom-44 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full border backdrop-blur-xl transition-all duration-300 hover:scale-105 shadow-lg ${
        isDark 
          ? 'bg-zinc-900/90 border-white/20 text-white hover:border-amber-500/50' 
          : 'bg-white/90 border-black/20 text-black hover:border-amber-500/50'
      }`}
      data-testid="recently-viewed-toggle"
      aria-label={`View ${items.length} recently viewed products`}
    >
      <Clock className="w-4 h-4 text-amber-500" />
      <span className="text-[11px] font-medium">
        {items.length} Viewed
      </span>
    </button>
  );
};

// Main Widget Component
const RecentlyViewedWidget = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <RecentlyViewedToggle onClick={() => setIsOpen(true)} />
      <RecentlyViewedPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default RecentlyViewedWidget;
